import { HTML } from "imperative-html/dist/esm/elements-strict.js";
import { Prompt } from "./Prompt.js";
import { SongDocument } from "./SongDocument.js";
import { ChangeSong } from "./changes.js";
import { songTemplates, createTemplateSong } from "./SongTemplates.js";
const { div, h2, p, select, option, button } = HTML;

export class SongTemplatePrompt implements Prompt {
    private readonly _select = select({"aria-label": "Song template", style: "width: 100%;"}, ...songTemplates.map(template => option({value: template.id}, template.name)));
    private readonly _description = p();
    private readonly _settings = p({style: "font-weight: 600;"});
    private readonly _structure = p();
    private readonly _instruments = p();
    private readonly _apply = button({type: "button"}, "Use template");
    private readonly _cancel = button({type: "button", class: "cancelButton", "aria-label": "Close dialog"});
    public readonly container = div({class: "prompt noSelection template-prompt", style: "width: 360px; max-width: calc(100vw - 48px); text-align: left;"},
        h2("New from template"),
        p("Song structure and sounds, with every note left to you."),
        div({class: "selectContainer", style: "width: 100%;"}, this._select),
        this._settings, this._description, this._structure, this._instruments,
        p({style: "font-size: 12px;"}, "Replaces the current song. Undo restores it. All patterns start empty; every setting stays editable."),
        this._apply, this._cancel,
    );
    private _focusTimer: ReturnType<typeof setTimeout>;
    constructor(private readonly _doc: SongDocument) {
        this._select.addEventListener("change", this._preview);
        this._apply.addEventListener("click", this._useTemplate);
        this._cancel.addEventListener("click", this._close);
        this._preview();
        this._focusTimer = setTimeout(() => this._select.focus());
    }
    private _preview = (): void => {
        const template = songTemplates.find(template => template.id === this._select.value)!;
        this._settings.textContent = template.settings;
        this._description.textContent = template.description;
        let bar = 1;
        this._structure.textContent = template.sections.map(section => {
            const range = `${section.name} ${bar}–${bar + section.patterns.length - 1}`;
            bar += section.patterns.length;
            return range;
        }).join(" → ");
        this._instruments.textContent = "Channels: " + template.instruments.join(" · ");
    };
    private _useTemplate = (): void => {
        const template = songTemplates.find(template => template.id === this._select.value)!;
        const song = createTemplateSong(template);
        this._doc.prompt = null;
        this._doc.goBackToStart();
        this._doc.selection.resetBoxSelection();
        this._doc.record(new ChangeSong(this._doc, song.toBase64String()), true, true);
        for (const channel of this._doc.song.channels) channel.muted = false;
    };
    private _close = (): void => { this._doc.undo(); };
    public cleanUp(): void {
        clearTimeout(this._focusTimer);
        this._select.removeEventListener("change", this._preview);
        this._apply.removeEventListener("click", this._useTemplate);
        this._cancel.removeEventListener("click", this._close);
    }
}
